<template>
    <div class="min-h-screen bg-background text-primary flex flex-col items-center py-12 px-4">
        <div class="max-w-2xl w-full card-primary">
            <h1 class="text-3xl font-bold text-gold text-center mb-6">
                💰 Calculateur de richesse 2025
            </h1>
            <p class="text-center text-secondary mb-8">
                Découvrez votre position dans la population française selon votre revenu et votre patrimoine.
            </p>

            <!-- Formulaire -->
            <form @submit.prevent="calculateWealth" class="space-y-4">
                <div>
                    <label class="block text-sm font-semibold mb-1">Revenu mensuel net (€)</label>
                    <input v-model.number="form.revenue" type="number" placeholder="Ex: 2500"
                        class="w-full border border-gold rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold bg-backgroundLight"
                        required />
                </div>

                <div>
                    <label class="block text-sm font-semibold mb-1">Patrimoine total estimé (€)</label>
                    <input v-model.number="form.assets" type="number" placeholder="Ex: 80000"
                        class="w-full border border-gold rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold bg-backgroundLight" />
                </div>

                <div class="flex gap-4">
                    <div class="flex-1">
                        <label class="block text-sm font-semibold mb-1">Âge</label>
                        <input v-model.number="form.age" type="number" placeholder="Ex: 35"
                            class="w-full border border-gold rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold bg-backgroundLight" />
                    </div>

                    <div class="flex-1">
                        <label class="block text-sm font-semibold mb-1">Situation familiale</label>
                        <select v-model="form.family"
                            class="w-full border border-gold rounded-lg px-3 py-2 bg-backgroundLight focus:outline-none focus:ring-2 focus:ring-gold">
                            <option value="single">Célibataire</option>
                            <option value="couple">En couple</option>
                            <option value="family">Famille avec enfants</option>
                        </select>
                    </div>
                </div>

                <button class="btn-gold w-full mt-4" type="submit" :disabled="loading">
                    <span v-if="!loading">Calculer mon niveau de richesse</span>
                    <span v-else class="flex items-center justify-center gap-2">
                        <div class="loader-sm"></div> Calcul en cours...
                    </span>
                </button>
            </form>

            <!-- Résultats -->
            <div v-if="result" class="mt-10 card-gold-premium text-center animate-slide-down">
                <h2 class="text-2xl font-bold text-gold mb-4">Résultat</h2>

                <p class="text-lg mb-4 text-white">
                    Revenu : {{ formatPercentile(result.revenuePercentile) }}
                </p>
                <p class="text-lg mb-6 text-white">
                    Patrimoine : {{ formatPercentile(result.assetsPercentile) }}
                </p>

                <div class="w-full bg-secondary/20 h-4 rounded-full overflow-hidden relative mb-6">
                    <div class="bg-gold h-4 rounded-full transition-all duration-700"
                        :style="{ width: result.revenuePercentile + '%' }"></div>
                </div>

                <p class="text-sm text-secondary italic">
                    Comparé à la moyenne nationale : revenu {{ result.revenueComparison }} / patrimoine {{
                        result.assetsComparison }}.
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from "vue";

// --- Données simplifiées (France, centiles approximatifs) ---
const revenueDeciles = [950, 1300, 1600, 1950, 2300, 2800, 3300, 3700, 4700, 6000];
const assetsDeciles = [0, 5000, 20000, 40000, 70000, 120000, 200000, 350000, 600000, 1000000];

const form = ref({
    revenue: null,
    assets: null,
    age: null,
    family: "single",
});

const loading = ref(false);
const result = ref(null);

// --- Calcul du niveau de richesse ---
const calculateWealth = async () => {
    loading.value = true;
    result.value = null;
    await new Promise((r) => setTimeout(r, 600)); // petite pause "calcul"

    // Coeff équivalence (simplifié)
    const coef = form.value.family === "couple" ? 1.5 : form.value.family === "family" ? 2 : 1;

    const adjustedRevenue = (form.value.revenue || 0) / coef;
    const adjustedAssets = form.value.assets || 0;

    // Centiles
    const revPercentile = getPercentile(adjustedRevenue, revenueDeciles);
    const assetPercentile = getPercentile(adjustedAssets, assetsDeciles);

    // Comparatif texte
    const revComp = adjustedRevenue > 1950 ? "au-dessus de la moyenne" : "sous la moyenne";
    const assetComp = adjustedAssets > 120000 ? "supérieur à la moyenne" : "inférieur à la moyenne";

    result.value = {
        revenuePercentile: revPercentile,
        assetsPercentile: assetPercentile,
        revenueComparison: revComp,
        assetsComparison: assetComp,
    };

    loading.value = false;
};

// --- Calcule le centile selon les déciles ---
function getPercentile(value, deciles) {
    if (value <= deciles[0]) return 10;
    for (let i = 1; i < deciles.length; i++) {
        if (value < deciles[i]) return i * 10;
    }
    return 100;
}

// --- Formattage lisible ---
function formatPercentile(value) {
    if (value <= 10) return "vous faites partie des 10% les plus pauvres";
    if (value <= 20) return "vous faites partie des 20% les plus pauvres";
    if (value <= 30) return "vous gagnez plus que 30% des Français";
    if (value <= 40) return "vous gagnez plus que 40% des Français";
    if (value <= 50) return "vous êtes autour de la médiane";
    if (value <= 60) return "vous gagnez plus que 60% des Français";
    if (value <= 70) return "vous gagnez plus que 70% des Français";
    if (value <= 80) return "vous gagnez plus que 80% des Français";
    if (value <= 90) return "vous faites partie des 10% les plus riches";
    return "vous faites partie des 5% les plus riches";
}
</script>
